import { useState, useEffect, useCallback } from 'react';

import { FilterType, ReadOnlyFilter, clearFilter } from './filter-utils';

function useFilterInitialization(filters: FilterType[]) {
  const [tempFilters, setTempFilters] = useState<ReadOnlyFilter[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const removeOnChange = filters.map((filter: FilterType) => {
      const { onChange, ...rest } = filter;
      return { ...rest };
    });

    const clonedFilters = window.structuredClone(removeOnChange);
    setTempFilters(clonedFilters);
    setIsInitialized(true);
  }, [filters]);

  return { tempFilters, setTempFilters, isInitialized };
}

function useDateFilterUpdater(setTempFilters: React.Dispatch<React.SetStateAction<ReadOnlyFilter[]>>) {
  const updateTempDateFilter = useCallback(
    (id: string, key: 'from' | 'to', value: Date | null) => {
      setTempFilters((prev) =>
        prev.map((filter) =>
          filter.id === id && filter.kind === 'date' ? { ...filter, value: { ...filter.value, [key]: value } } : filter
        )
      );
    },
    [setTempFilters]
  );

  return updateTempDateFilter;
}

function useTextFilterUpdater(setTempFilters: React.Dispatch<React.SetStateAction<ReadOnlyFilter[]>>) {
  const updateTempTextFilter = useCallback(
    (id: string, value: string | null) => {
      setTempFilters((prev) =>
        prev.map((filter) => (filter.id === id && filter.kind === 'text' ? { ...filter, value } : filter))
      );
    },
    [setTempFilters]
  );

  return updateTempTextFilter;
}

function useSelectFilterUpdater(setTempFilters: React.Dispatch<React.SetStateAction<ReadOnlyFilter[]>>) {
  const updateTempSelectFilter = useCallback(
    (id: string, value: string | null) => {
      setTempFilters((prev) =>
        prev.map((filter) => (filter.id === id && filter.kind === 'select' ? { ...filter, value } : filter))
      );
    },
    [setTempFilters]
  );

  return updateTempSelectFilter;
}

function useMultiSelectFilterUpdater(setTempFilters: React.Dispatch<React.SetStateAction<ReadOnlyFilter[]>>) {
  const updateTempMultiSelectFilter = useCallback(
    (id: string, value: string[] | null) => {
      setTempFilters((prev) =>
        prev.map((filter) => (filter.id === id && filter.kind === 'multi-select' ? { ...filter, value } : filter))
      );
    },
    [setTempFilters]
  );

  return updateTempMultiSelectFilter;
}

function useFilterApplier(filters: FilterType[], tempFilters: ReadOnlyFilter[]) {
  const applyFilters = useCallback(() => {
    tempFilters.forEach((tempFilter) => {
      const actualFilter = filters.find((f) => f.id === tempFilter.id);
      if (!actualFilter) return;

      if (
        (tempFilter.kind === 'text' && actualFilter.kind === 'text') ||
        (tempFilter.kind === 'select' && actualFilter.kind === 'select')
      ) {
        actualFilter.onChange(tempFilter.value);
      } else if (tempFilter.kind === 'multi-select' && actualFilter.kind === 'multi-select') {
        actualFilter.onChange(tempFilter.value);
      } else if (tempFilter.kind === 'date' && actualFilter.kind === 'date') {
        actualFilter.onChange.from(tempFilter.value.from);
        actualFilter.onChange.to(tempFilter.value.to);
      }
    });
  }, [filters, tempFilters]);

  return applyFilters;
}

function useFilterState(filters: FilterType[]) {
  const { tempFilters, setTempFilters, isInitialized } = useFilterInitialization(filters);
  const updateTempDateFilter = useDateFilterUpdater(setTempFilters);
  const updateTempTextFilter = useTextFilterUpdater(setTempFilters);
  const updateTempSelectFilter = useSelectFilterUpdater(setTempFilters);
  const updateTempMultiSelectFilter = useMultiSelectFilterUpdater(setTempFilters);
  const applyFilters = useFilterApplier(filters, tempFilters);

  return {
    applyFilters,
    clearFilter,
    isInitialized,
    tempFilters,
    updateTempDateFilter,
    updateTempMultiSelectFilter,
    updateTempSelectFilter,
    updateTempTextFilter,
  };
}

export { useFilterApplier, useFilterState };
